/**
 * Wattec Custom PC Builder — spec chips on the Extras cards
 *
 * The monitor, keyboard, mouse and headphone cards each carry a long spec
 * line ("3840x2160, QD-OLED, 240Hz, 0.03ms, … | Resolution: 4K | Panel
 * Type: OLED | …"), clipped to a few lines on the card. This reads that line
 * and lays the few figures that decide a purchase — resolution, panel,
 * refresh rate and response time for a monitor; connection, switches and
 * size for a keyboard; and so on — as a row of small chips under the
 * product's name, so a whole list can be compared at a glance.
 *
 * It only reads what Easify has already drawn and adds one element of its
 * own to each card, after the name. The card's text still starts with the
 * product's name, which is what the builder's other scripts match cards on,
 * and the spec line itself is left in place. Easify's markup, classes,
 * pricing and selection are untouched.
 */
(function () {
  'use strict';

  var CHIPS_CLASS = 'wt-specchips';
  var CHIP_CLASS = 'wt-specchip';
  var MAX_CHIPS = 6;

  /* ---- small helpers ------------------------------------------------ */

  function flatten(text) {
    return String(text == null ? '' : text).replace(/\s+/g, ' ').trim();
  }

  /**
   * Splits a spec line into its free text (everything before the first "|")
   * and its "Key: Value" fields, keyed in lower case.
   */
  function parse(line) {
    var parts = flatten(line).split('|');
    var fields = {};
    for (var i = 1; i < parts.length; i++) {
      var at = parts[i].indexOf(':');
      if (at < 0) continue;
      fields[flatten(parts[i].slice(0, at)).toLowerCase()] = flatten(parts[i].slice(at + 1));
    }
    return { text: flatten(parts[0]), fields: fields };
  }

  /** The first pattern that matches the text, as the chip it stands for. */
  function firstOf(text, list) {
    for (var i = 0; i < list.length; i++) {
      if (list[i][0].test(text)) return list[i][1];
    }
    return '';
  }

  function connection(spec) {
    var conn = spec.fields.connection || '';
    if (/wireless/i.test(conn)) return 'Wireless';
    if (/wired/i.test(conn)) {
      // Headsets: which plug, since a 3.5mm-only headset needs a jack.
      var plug = spec.text.match(/^wired\s+(usb\/3\.5mm|usb|3\.5mm)/i);
      return plug ? 'Wired ' + plug[1].replace('/', ' / ').replace('usb', 'USB') : 'Wired';
    }
    return '';
  }

  function bluetooth(spec) {
    return /bluetooth/i.test(spec.text) ? 'Bluetooth' : '';
  }

  function battery(spec) {
    var m = spec.text.match(/(\d+)\s*h\b(?:ours)?\s*battery/i);
    return m ? m[1] + 'h battery' : '';
  }

  /* ---- what matters for each kind of part --------------------------- */

  var KINDS = [
    {
      test: /monitor/i,
      chips: function (spec) {
        var f = spec.fields;
        var res = f.resolution || '';
        res = /qhd|1440/i.test(res) ? '1440p' : /fhd|1080/i.test(res) ? '1080p' : /uhd|4k|2160/i.test(res) ? '4K' : res;

        var panel = firstOf(spec.text, [
          [/qd-?oled/i, 'QD-OLED'],
          [/w-?oled/i, 'WOLED'],
          [/mini-?led[^,]*\bva\b/i, 'Mini-LED VA'],
          [/mini-?led/i, 'Mini-LED IPS'],
          [/rapid ips/i, 'Rapid IPS'],
          [/fast ips/i, 'Fast IPS'],
          [/super tn/i, 'Super TN'],
          [/\boled\b/i, 'OLED'],
          [/\bips\b/i, 'IPS'],
          [/\bva\b/i, 'VA'],
          [/\btn\b/i, 'TN']
        ]) || f['panel type'] || '';

        // The fastest rate the text quotes (a "165-180Hz" panel is 180Hz,
        // an overclocked one its overclock); a dual-mode panel shows both.
        var rates = (spec.text.match(/\d{2,3}(?=\s*hz)/gi) || []).map(Number);
        var hz = rates.length ? Math.max.apply(null, rates) : parseInt(f['refresh rate'], 10);
        var refresh = '';
        if (/dual-?mode/i.test(spec.text) && rates.length > 1) {
          refresh = Math.min.apply(null, rates) + ' / ' + hz + 'Hz';
        } else if (hz) {
          refresh = hz + 'Hz';
        }

        var ms = spec.text.match(/(\d*\.?\d+)\s*ms/i);
        return [res, panel, refresh, ms ? ms[1] + 'ms' : '', f['screen size'] || ''];
      }
    },
    {
      test: /keyboard/i,
      chips: function (spec) {
        var f = spec.fields;
        var sw = f['switch type'] || '';
        var type = /optical/i.test(sw) && /analog/i.test(sw) ? 'Analog optical'
          : /mecha-?membrane/i.test(sw) ? 'Mecha-membrane'
          : /optical/i.test(sw) && !/mechanical/i.test(sw) ? 'Optical'
          : /mechanical/i.test(sw) ? 'Mechanical'
          : /membrane/i.test(sw) ? 'Membrane' : '';
        var feel = firstOf(spec.text + ' ' + sw, [
          [/clicky|\bgreen\b|\bblue\b/i, 'Clicky'],
          [/tactile|\bbrown\b/i, 'Tactile'],
          [/linear|\bred\b/i, 'Linear']
        ]);
        var form = f['form factor'] || '';
        var pct = form.match(/\d{2,3}%/);
        var size = /tkl|tenkeyless/i.test(form) ? 'TKL' : pct ? pct[0] + ' size' : /full/i.test(form) ? 'Full-size' : '';
        return [
          connection(spec), bluetooth(spec), type, feel,
          /low-?profile/i.test(spec.text + sw) ? 'Low-profile' : '',
          /hot-?swap/i.test(spec.text + sw) ? 'Hot-swap' : '', size
        ];
      }
    },
    {
      test: /mouse/i,
      chips: function (spec) {
        var f = spec.fields;
        var dpi = (f.sensor || spec.text).match(/([\d,]+)\s*dpi/i);
        return [connection(spec), bluetooth(spec), dpi ? dpi[1] + ' DPI' : '', f.weight || '', battery(spec)];
      }
    },
    {
      test: /headphone|headset/i,
      chips: function (spec) {
        var f = spec.fields;
        var spatial = firstOf(spec.text, [
          [/dolby atmos/i, 'Dolby Atmos'],
          [/thx spatial/i, 'THX Spatial'],
          [/dts/i, 'DTS spatial'],
          [/7\.1/, '7.1 surround']
        ]);
        var mic = firstOf(f.microphone || '', [
          [/retractable/i, 'Retractable mic'],
          [/detachable/i, 'Detachable mic'],
          [/built-?in/i, 'Built-in mic'],
          [/fixed/i, 'Fixed mic']
        ]);
        return [
          connection(spec), bluetooth(spec),
          /active noise cancell/i.test(spec.text) ? 'ANC' : '',
          spatial, f['driver size'] ? f['driver size'] + ' drivers' : '', mic, battery(spec)
        ];
      }
    }
  ];

  /* ---- the chips this file adds --------------------------------------- */

  function kindFor(group) {
    var label = group.querySelector('.tpo_option-label-container .form__label');
    var text = label ? label.textContent : '';
    for (var i = 0; i < KINDS.length; i++) {
      if (KINDS[i].test.test(text)) return KINDS[i];
    }
    return null;
  }

  function build(chips) {
    var row = document.createElement('span');
    row.className = CHIPS_CLASS;
    // The spec line beside it already carries the same words for screen
    // readers, so the chips are a visual summary only.
    row.setAttribute('aria-hidden', 'true');
    for (var i = 0; i < chips.length; i++) {
      var chip = document.createElement('span');
      chip.className = CHIP_CLASS;
      chip.textContent = chips[i];
      row.appendChild(chip);
    }
    return row;
  }

  function decorate(card, kind) {
    if (card.querySelector('.' + CHIPS_CLASS)) return;
    var name = card.querySelector('.tpo_option_type_label');
    var desc = card.querySelector('.tpo_option_type_description.below_value')
      || card.querySelector('.tpo_option_type_description');
    if (!name || !desc) return;

    var spec = parse(desc.textContent);
    // "No thanks" entries have no fields, and get no chips.
    if (!Object.keys(spec.fields).length) return;

    var chips = kind.chips(spec).filter(function (c, i, all) {
      return c && all.indexOf(c) === i;
    }).slice(0, MAX_CHIPS);
    if (!chips.length) return;

    name.parentNode.insertBefore(build(chips), name.nextSibling);
  }

  /* A list's cards: Easify's dropdown cards, or the tick-box cards of a list
     that lets several be chosen (the monitors, so two can be bundled). */
  var CARDS = '.tpo_option-type-selection:not(.tpo_option-type-selection--empty), .tpo_radio-button-wrapper';

  function decorateAll() {
    var groups = document.querySelectorAll('.tpo_option-container');
    for (var g = 0; g < groups.length; g++) {
      var kind = kindFor(groups[g]);
      if (!kind) continue;
      var cards = groups[g].querySelectorAll(CARDS);
      for (var c = 0; c < cards.length; c++) {
        try { decorate(cards[c], kind); } catch (e) { /* one odd card never stops the rest */ }
      }
    }
  }

  /* ---- staying in step with Easify ------------------------------------
   * Easify can redraw a list (a conditional option appearing, say), which
   * drops the chips with the old cards, so its container is watched and any
   * card without chips gets them again. A card that already has chips is
   * left alone, so this never sets off its own watcher in a loop.
   * ------------------------------------------------------------------ */

  function start() {
    var root = document.querySelector('.tpo_option-set-container')
      || document.querySelector('.tpo_option-set-wrapper');
    if (!root) return false;

    decorateAll();

    if (window.MutationObserver) {
      var timer = null;
      new MutationObserver(function () {
        clearTimeout(timer);
        timer = setTimeout(decorateAll, 120);
      }).observe(root, { childList: true, subtree: true });
    }
    return true;
  }

  /* Easify draws its options after the page has loaded, so this waits for
     them to appear, and gives up quietly after half a minute. */
  function waitForEasify() {
    if (start()) return;
    var tries = 0;
    var poll = setInterval(function () {
      tries++;
      if (start() || tries > 60) clearInterval(poll);
    }, 500);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', waitForEasify);
  } else {
    waitForEasify();
  }
})();
